import { useCallback, useRef, useState } from "react";

interface Props {
  accept?: string;
  maxSizeMB?: number;
  onFile: (file: File) => void;
  disabled?: boolean;
}

export default function DropZone({
  accept = "application/pdf",
  maxSizeMB = 100,
  onFile,
  disabled = false,
}: Props) {
  const [hovering, setHovering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    (f: File) => {
      setError(null);
      if (accept && !f.type.includes("pdf") && !f.name.toLowerCase().endsWith(".pdf")) {
        setError("Nur PDF-Dateien sind erlaubt.");
        return;
      }
      const sizeMB = f.size / (1024 * 1024);
      if (sizeMB > maxSizeMB) {
        setError(`Datei zu groß (${sizeMB.toFixed(1)} MB > ${maxSizeMB} MB).`);
        return;
      }
      onFile(f);
    },
    [accept, maxSizeMB, onFile]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setHovering(false);
      if (disabled) return;
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [disabled, handleFile]
  );

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) handleFile(file);
      // Input zurücksetzen damit dasselbe File nochmal pickbar ist
      e.target.value = "";
    },
    [handleFile]
  );

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setHovering(true);
        }}
        onDragLeave={() => setHovering(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label="PDF-Datei wählen oder per Drag & Drop hier ablegen"
        className={`
          relative h-52 rounded-lg border-2 border-dashed transition-colors
          flex flex-col items-center justify-center gap-2 p-6 text-center
          ${disabled ? "opacity-50 cursor-not-allowed border-slate-200 bg-slate-50" : ""}
          ${
            !disabled && hovering
              ? "border-brand-500 bg-brand-50"
              : !disabled
                ? "border-slate-300 bg-white hover:border-slate-400 hover:bg-slate-50 cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                : ""
          }
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onPick}
          className="sr-only"
          disabled={disabled}
        />
        <svg
          className="w-10 h-10 text-slate-400"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
          />
        </svg>
        <div className="text-sm">
          <span className="font-medium text-slate-700">Plan hierher ziehen</span>
          <span className="text-slate-500"> oder klicken zum Auswählen</span>
        </div>
        <div className="text-xs text-slate-400">
          PDF · max {maxSizeMB} MB
        </div>
      </div>
      {error && (
        <div role="alert" className="text-xs text-red-700 px-1">
          {error}
        </div>
      )}
    </div>
  );
}
